import { forwardRef, useContext } from "react";
import TaskAlertTimestampContext from "./TaskAlertTimestampContext";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { TASK_ALERT_ESTIMATED_TIME } from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import TaskAlertProgress from "./TaskAlertProgress";

const STATUS_ICON = {
  inProgress: ({ sx }) => (
    <Loader2
      size={16}
      style={{
        flexShrink: 0,
        animation: "taskAlertSpin 1s linear infinite",
        ...sx,
      }}
    />
  ),
  success: ({ sx }) => (
    <CheckCircle2 size={16} style={{ flexShrink: 0, ...sx }} />
  ),
  error: ({ sx }) => <AlertCircle size={16} style={{ flexShrink: 0, ...sx }} />,
  warning: ({ sx }) => (
    <AlertCircle size={16} style={{ flexShrink: 0, ...sx }} />
  ),
  pending: () => null,
};

/**
 * Shared visual card for task/alert items in the notifications panel.
 */
const TaskAlertCard = forwardRef(
  (
    {
      status = "inProgress",
      label,
      progress = 0,
      onClick,
      cursor = "default",
      showProgressBar = true,
      showProgressPercent = true,
      progressSlot = null,
      details = null,
      actions = null,
      /** Formatted remaining time as mm:ss; card owns the i18n label. */
      estimatedTime = null,
      /** Explicit finish date (ms/ISO). Falls back to TaskAlertTimestampContext. */
      timestamp = null,
      sx = {},
    },
    ref
  ) => {
    const { t, i18n } = useTranslationApp();
    const contextTimestamp = useContext(TaskAlertTimestampContext);
    const isFinished =
      status === "success" || status === "error" || status === "warning";
    const resolvedTimestamp = timestamp ?? contextTimestamp;
    const finishedDate =
      isFinished && resolvedTimestamp ? new Date(resolvedTimestamp) : null;
    const formattedFinishedAt =
      finishedDate && !Number.isNaN(finishedDate.getTime())
        ? finishedDate.toLocaleString(i18n.language || undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          })
        : null;
    const StatusIcon = STATUS_ICON[status] || STATUS_ICON.inProgress;
    const roundedProgress = Math.round(progress);
    const labelColor = status === "pending" ? "text.secondary" : "text.primary";
    const hasEstimatedTime = Boolean(estimatedTime);
    const showPercent = showProgressPercent && !progressSlot;

    const iconColor =
      status === "success"
        ? "success.main"
        : status === "error"
          ? "error.main"
          : status === "warning"
            ? "warning.main"
            : "text.secondary";

    return (
      <Box
        ref={ref}
        onClick={onClick}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          borderRadius: 2,
          border: "1px solid",
          borderColor: INPUT_COLOR_BORDER,
          bgcolor: "background.paper",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
          p: 2,
          width: "100%",
          cursor,
          "@keyframes taskAlertSpin": {
            from: { transform: "rotate(0deg)" },
            to: { transform: "rotate(360deg)" },
          },
          ...sx,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: hasEstimatedTime ? "flex-start" : "center",
              gap: 1.25,
              minWidth: 0,
              flex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: iconColor,
                flexShrink: 0,
                ...(hasEstimatedTime && { mt: 0.4 }),
              }}
            >
              <StatusIcon sx={{ color: "inherit" }} />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
              }}
            >
              <Box
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: labelColor,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                }}
              >
                {label}
              </Box>
              {hasEstimatedTime ? (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.4,
                  }}
                >
                  {t(TASK_ALERT_ESTIMATED_TIME, {
                    estimatedTime,
                  })}
                </Typography>
              ) : null}
            </Box>
          </Box>
          {formattedFinishedAt ? (
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.7rem",
                color: "text.secondary",
                fontVariantNumeric: "tabular-nums",
                // Same line box as the label (0.875rem × 1.5) so the date is
                // vertically centered on the first line of the title.
                lineHeight: "1.5155rem",
                flexShrink: 0,
                whiteSpace: "nowrap",
                alignSelf: "flex-start",
              }}
            >
              {formattedFinishedAt}
            </Typography>
          ) : null}
        </Box>

        {details}

        {progressSlot ||
          (showProgressBar && (
            <TaskAlertProgress value={progress} status={status} />
          ))}

        {showPercent ? (
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              fontVariantNumeric: "tabular-nums",
              color: "text.secondary",
              textAlign: "right",
              lineHeight: 1.2,
              mt: -0.5,
            }}
          >
            {roundedProgress}%
          </Typography>
        ) : null}

        {actions ? (
          <Box sx={{ pt: 0.5, ...(showPercent && { mt: -1.25 }) }}>
            {actions}
          </Box>
        ) : null}
      </Box>
    );
  }
);

TaskAlertCard.displayName = "TaskAlertCard";

export default TaskAlertCard;
